import { format, utcToZonedTime } from 'date-fns-tz';
import { getVerb } from 'modules/campaigns/alerts/components/AlertList/AlertList.utils';
import { METRICS_TO_COMPARATORS } from 'modules/campaigns/alerts/components/constants';
import { ThresholdType } from 'modules/campaigns/alerts/constants';
import { getThresholdForSummary } from 'modules/campaigns/alerts/modals/UpdateAlertsModal/useUpdatedAlertsSummary';
import { ThresholdInputValue } from 'modules/campaigns/alerts/types';
import {
  getComparator,
  getMetricName,
  getPreposition,
  getTypeKey,
} from 'modules/campaigns/alerts/utils';
import { CampaignTypeFilter } from 'modules/domains/campaign';
import { CampaignAlert } from '../../hooks/useCampaignsTriggeredAlerts';
import { pluralizeWord } from '../../utils';

export const parseTriggeredValue = (
  triggeredValue: number,
  thresholdType: ThresholdType,
): ThresholdInputValue => {
  const isPercentage = thresholdType === ThresholdType.Percentage;

  return isPercentage && triggeredValue !== 0
    ? (`${triggeredValue.toFixed(2)}` as ThresholdInputValue)
    : `${triggeredValue}`;
};

/**
 * Get the display text for an alert history
 * @param alert
 * @param isThreshold - whether to use threshold value or triggered value
 */
export const getAlertHistoryItemLabelDisplayText = (alert: CampaignAlert, isThreshold: boolean) => {
  const { metricName, threshold, triggeredValue, frequency, metricDisplayType, campaignType } =
    alert;

  const thresholdType = getTypeKey(metricDisplayType);

  const thresholdDisplayText = getThresholdForSummary(
    `${isThreshold ? threshold : parseTriggeredValue(triggeredValue, thresholdType)}`,
    thresholdType,
  );
  const metricDisplayName = getMetricName(metricName);
  const verb = getVerb(metricDisplayType);

  const comparatorDisplayText = getComparator(METRICS_TO_COMPARATORS[metricName]);
  const frequencyNumber = Number(frequency.replace(/PT|H/g, '')); // PT1H -> 1
  const frequencyDisplayText = `${frequencyNumber} ${pluralizeWord('hour', frequencyNumber)}`;

  const isScheduledCampaign = [CampaignTypeFilter.Blast, CampaignTypeFilter.Eligibility].includes(
    campaignType,
  );

  const preposition = isThreshold
    ? getPreposition(isScheduledCampaign)
    : getPreposition(isScheduledCampaign, false);

  return {
    thresholdText: `${metricDisplayName} ${verb} ${
      isThreshold ? comparatorDisplayText : 'at'
    } ${thresholdDisplayText}`,
    frequencyText: `${preposition} ${frequencyDisplayText} of sending`,
  };
};

export const getAlertHistoryItemCaptionDisplayText = (triggeredTime: string, timeZone: string) => {
  return format(utcToZonedTime(triggeredTime, timeZone), 'MMM d, y, h:mm a z', {
    timeZone,
  });
};
